/**
 * RegisterPage — invitation-token registration flow (R3b)
 *
 * URL: /register?token=<invitation-token>
 *
 * Three entry states:
 *  1. No token          → "Invitation required" screen
 *  2. Token + validating → spinner / skeleton
 *  3. Token + valid      → registration form with pre-filled email + Turnstile
 *  4. Token + invalid    → "Invitation expired / used" screen
 *
 * Visual shell mirrors LoginPage (split-screen, brand-glow, surface-raised-2
 * right panel, Space Grotesk headings) for a coherent auth system.
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserIcon } from '@heroicons/react/24/outline';
import PasswordToggleButton from '@/components/ui/PasswordToggleButton';
import {
  previewInvitation,
  redeemInvitation,
  storeTokens,
  getMe,
} from '@/services/authApi';
import type { InvitationPreview } from '@/services/authApi';
import Button from '@/components/Button';

// ── Turnstile globals ─────────────────────────────────────────────────────────

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

declare global {
  interface Window {
    turnstile?: {
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      render: (container: string | HTMLElement, params: Record<string, unknown>) => string;
    };
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRole(role: 'coach' | 'athlete'): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

/** Extract a user-friendly message from an API error response.
 *
 * Handles two `detail` shapes from the global error handler (commit 7ac4d2b):
 *   - Legacy string:  { detail: "some message" }
 *   - Nested object:  { detail: { error: "code", message: "human text" } }
 */
function extractApiError(error: unknown): { code: string | null; message: string } {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: { data?: { detail?: string | { error?: string; message?: string }; error?: string } };
    };
    const data = axiosError.response?.data;

    // Nested object shape: { detail: { error: "code", message: "..." } }
    if (data?.detail && typeof data.detail === 'object') {
      const nested = data.detail as { error?: string; message?: string };
      return {
        code: nested.error ?? null,
        message: nested.message ?? 'Something went wrong. Please try again.',
      };
    }

    // Legacy string shape: { detail: "...", error?: "code" }
    const code = data?.error ?? null;
    const detail = data?.detail;
    if (typeof detail === 'string') return { code, message: detail };
  }
  if (error instanceof Error) return { code: null, message: error.message };
  return { code: null, message: 'Something went wrong. Please try again.' };
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Shared split-screen shell that mirrors LoginPage's layout exactly. */
function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <style>{`
        @keyframes mc-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mc-register-form {
          animation: mc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .mc-input-focus input:focus,
        .mc-input-focus input:focus-visible {
          outline: none;
          border-color: var(--color-brand-primary);
          box-shadow: 0 0 0 2px rgba(59,130,246,0.22);
        }
        .mc-input-disabled {
          opacity: 0.55;
          cursor: not-allowed;
        }
        .mc-submit-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .mc-submit-btn:not(:disabled):hover {
          background-color: var(--color-brand-primary-hover);
          box-shadow: 0 0 18px rgba(59,130,246,0.30), 0 4px 12px rgba(0,0,0,0.20);
        }
        .mc-submit-btn:not(:disabled):active {
          transform: scale(0.98);
        }
        .mc-back-link {
          color: var(--color-brand-primary);
          text-decoration: none;
          font-weight: 500;
          transition: color 150ms ease;
        }
        .mc-back-link:hover {
          color: var(--color-brand-primary-hover);
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          {/* Radial brand-glow anchored upper-right */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          {/* Secondary soft glow, lower-left */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '160px',
                height: '160px',
                objectFit: 'contain',
                borderRadius: '32px',
                boxShadow:
                  '0 0 48px rgba(59,130,246,0.28), 0 8px 32px rgba(0,0,0,0.35)',
              }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                }}
              >
                Movement analysis, measured.
              </h1>
              <p
                className="text-body-md"
                style={{ color: 'var(--color-text-secondary)', margin: 0 }}
              >
                Create your PointeTracer account.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (form / state) ────────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div className="mc-register-form w-full" style={{ maxWidth: '400px' }}>
            {/* Mobile-only logo */}
            <div
              className="flex lg:hidden"
              style={{ justifyContent: 'center', marginBottom: '32px' }}
            >
              <img
                src="/pwa-512.png"
                alt="PointeTracer logo"
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                  borderRadius: '16px',
                  boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                }}
              />
            </div>

            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/** Spinner used for the validating skeleton. */
function Spinner({ size = 20 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block',
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        border: '2px solid var(--color-border-strong)',
        borderTopColor: 'var(--color-brand-primary)',
        animation: 'spin 0.75s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

/** Error/info banner used inside the form. */
function AlertBanner({
  children,
  kind = 'error',
}: {
  children: React.ReactNode;
  kind?: 'error' | 'info';
}) {
  const colors =
    kind === 'error'
      ? {
          border: 'rgba(248,113,113,0.30)',
          bg: 'rgba(248,113,113,0.08)',
          text: 'var(--color-signal-bad)',
        }
      : {
          border: 'rgba(96,165,250,0.30)',
          bg: 'rgba(96,165,250,0.08)',
          text: 'var(--color-signal-info)',
        };
  return (
    <div
      role="alert"
      style={{
        borderRadius: '10px',
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        padding: '12px 14px',
        fontSize: '13px',
        color: colors.text,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

// ── Screen: no token ──────────────────────────────────────────────────────────

function NoTokenScreen() {
  return (
    <AuthShell>
      <div style={{ marginBottom: '40px' }}>
        <h2
          className="text-display-lg"
          style={{
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
            letterSpacing: '-0.5px',
          }}
        >
          Invitation required
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          PointeTracer is invitation-only.
        </p>
      </div>

      <div
        style={{
          borderRadius: '12px',
          border: '1px solid var(--color-border-subtle)',
          backgroundColor: 'var(--color-surface-raised-3)',
          padding: '20px 18px',
          marginBottom: '28px',
        }}
      >
        <p
          className="text-body-md"
          style={{ color: 'var(--color-text-secondary)', margin: 0, lineHeight: 1.7 }}
        >
          To request an invitation, email{' '}
          <a
            href="mailto:hello@pointetracer.com"
            style={{
              color: 'var(--color-brand-primary)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            hello@pointetracer.com
          </a>
          .
        </p>
      </div>

      <a href="/login" className="mc-back-link" style={{ fontSize: '14px' }}>
        Back to sign in
      </a>
    </AuthShell>
  );
}

// ── Screen: validating ────────────────────────────────────────────────────────

function ValidatingScreen() {
  return (
    <AuthShell>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '16px',
          padding: '60px 0',
        }}
      >
        <Spinner size={28} />
        <p className="text-body-md" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
          Verifying your invitation…
        </p>
      </div>
    </AuthShell>
  );
}

// ── Screen: invalid invitation ────────────────────────────────────────────────

/** Per-error-code heading, subtitle, and action hint. */
function _invalidTokenCopy(errorCode: string | undefined): {
  heading: string;
  subtitle: string;
  hint: string;
} {
  switch (errorCode) {
    case 'revoked':
      return {
        heading: 'Invitation cancelled',
        subtitle: 'This link has been deactivated.',
        hint: 'Contact your coach to request a fresh invitation link.',
      };
    case 'expired':
      return {
        heading: 'Invitation expired',
        subtitle: 'This link is past its expiry date.',
        hint: 'Ask your coach or admin to send a new invitation.',
      };
    case 'already_used':
      return {
        heading: 'Invitation already used',
        subtitle: 'This link has already been redeemed.',
        hint: 'If you already registered, sign in below. Otherwise contact your admin.',
      };
    default:
      return {
        heading: 'Invitation not valid',
        subtitle: 'This link cannot be used.',
        hint: 'If you believe this is a mistake, ask your coach or admin to send a new invitation.',
      };
  }
}

function InvalidTokenScreen({
  message,
  errorCode,
}: {
  message?: string;
  errorCode?: string;
}) {
  const { heading, subtitle, hint } = _invalidTokenCopy(errorCode);

  const displayMessage =
    message && message.trim().length > 0
      ? message
      : 'This invitation link is no longer valid.';

  return (
    <AuthShell>
      <div style={{ marginBottom: '40px' }}>
        <h2
          className="text-display-lg"
          style={{
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
            letterSpacing: '-0.5px',
          }}
        >
          {heading}
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          {subtitle}
        </p>
      </div>

      <AlertBanner kind="error">{displayMessage}</AlertBanner>

      <div style={{ marginTop: '20px' }}>
        <p className="text-body-sm" style={{ color: 'var(--color-text-muted)', margin: '0 0 16px' }}>
          {hint}
        </p>
        <a href="/login" className="mc-back-link" style={{ fontSize: '14px' }}>
          Back to sign in
        </a>
      </div>
    </AuthShell>
  );
}

// ── Screen: registration form ─────────────────────────────────────────────────

interface RegistrationFormProps {
  token: string;
  invitation: InvitationPreview;
}

function RegistrationForm({ token, invitation }: RegistrationFormProps) {
  const navigate = useNavigate();
  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'athlete' | 'coach'>(
    invitation.intended_role ?? 'athlete',
  );
  const [turnstileToken, setTurnstileToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Explicit Turnstile render. We don't rely on the cf-turnstile class
  // auto-render because it races React's mount in SPAs — the script may
  // sweep the DOM before our cf-turnstile div exists, then never re-sweep.
  // Polling for window.turnstile is the canonical SPA pattern per
  // https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const container = turnstileContainerRef.current;
    if (!container) return;

    let widgetId: string | null = null;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || !container) return;
      try {
        widgetId = window.turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (tkn: string) => setTurnstileToken(tkn),
          'error-callback': () => setTurnstileToken(''),
          'expired-callback': () => setTurnstileToken(''),
          theme: 'dark',
        });
        if (widgetId) container.dataset.turnstileWidgetId = widgetId;
      } catch (err) {
        // Most common cause: render called twice on the same container
        // during React StrictMode double-mount. Safe to ignore.
         
        console.warn('Turnstile render failed:', err);
      }
    };

    // Poll for the API. challenges.cloudflare.com/turnstile/v0/api.js is
    // loaded with `async defer` from index.html, so it may not be ready yet.
    if (window.turnstile) {
      render();
    } else {
      const interval = setInterval(() => {
        if (window.turnstile) {
          clearInterval(interval);
          render();
        }
      }, 100);
      // Give up after 15s — at that point the script clearly failed to load
      // (likely a CSP or network error) and the user will see the inline
      // "Complete the verification above" message.
      setTimeout(() => clearInterval(interval), 15000);
    }

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) {
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // remove() can throw if the widget is already gone — ignore.
        }
      }
    };
  }, []);

  const passwordTooShort = password.length > 0 && password.length < 12;
  const turnstileRequired = TURNSTILE_SITE_KEY.length > 0;
  const turnstileReady = !turnstileRequired || turnstileToken.length > 0;

  const isFormValid =
    displayName.trim().length > 0 && password.length >= 12 && turnstileReady;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    if (turnstileRequired && !turnstileToken) {
      setError('Please complete the CAPTCHA verification before submitting.');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const body: Parameters<typeof redeemInvitation>[1] = {
        display_name: displayName.trim(),
        password,
        role: selectedRole,
      };
      if (turnstileToken) {
        body.turnstile_token = turnstileToken;
      }

      const tokens = await redeemInvitation(token, body);
      storeTokens(tokens);
      // Fetch current user to hydrate AuthContext, then navigate to app
      await getMe();
      // If the invitation was tied to a channel, redirect there; otherwise fall
      // back to the default templates dashboard.
      const destination = tokens.post_registration_redirect ?? '/templates';
      navigate(destination, { replace: true });
    } catch (err) {
      const { code, message } = extractApiError(err);

      // Reset the Turnstile widget on CAPTCHA failure so the user can retry
      if (code === 'captcha_failed' && window.turnstile && turnstileContainerRef.current) {
        const widgetId = turnstileContainerRef.current.dataset.turnstileWidgetId;
        if (widgetId) window.turnstile.reset(widgetId);
        setTurnstileToken('');
        setError('CAPTCHA verification failed. Please try again.');
        setIsSubmitting(false);
        return;
      }

      if (code === 'invitation_used') {
        setError('This invitation has already been used.');
      } else if (code === 'invitation_expired') {
        setError('This invitation has expired.');
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell>
      {/* Heading */}
      <div style={{ marginBottom: '36px' }}>
        <h2
          className="text-display-lg"
          style={{
            color: 'var(--color-text-primary)',
            margin: '0 0 8px',
            letterSpacing: '-0.5px',
          }}
        >
          Create your account
        </h2>
        <p className="text-body-md" style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
          Welcome — let&apos;s get you set up.
        </p>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
      >
        {/* Error banner */}
        {error && <AlertBanner kind="error">{error}</AlertBanner>}

        {/* Email (read-only) */}
        <div>
          <label
            htmlFor="register-email"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Email
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="register-email"
              type="email"
              value={invitation.email}
              readOnly
              disabled
              title="This email was set by the admin who invited you."
              aria-label="Email address — set by the admin who invited you"
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-strong)',
                backgroundColor: 'var(--color-surface-raised-3)',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                padding: '0 14px',
                boxSizing: 'border-box',
                opacity: 0.55,
                cursor: 'not-allowed',
              }}
            />
          </div>
          <p
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              margin: '4px 0 0',
              lineHeight: 1.4,
            }}
          >
            Set by the admin who invited you — cannot be changed here.
          </p>
        </div>

        {/* Role selector — pre-filled from invitation; invitee can change */}
        <div>
          <label
            htmlFor="register-role"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Role
          </label>
          <select
            id="register-role"
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as 'athlete' | 'coach')}
            disabled={isSubmitting}
            aria-label="Your role"
            style={{
              width: '100%',
              height: '44px',
              borderRadius: '10px',
              border: '1px solid var(--color-border-strong)',
              backgroundColor: 'var(--color-surface-raised-3)',
              color: 'var(--color-text-primary)',
              fontSize: '14px',
              padding: '0 36px 0 14px',
              boxSizing: 'border-box',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              appearance: 'none',
              backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3E%3Cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'M6 8l4 4 4-4\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 10px center',
              backgroundSize: '16px',
              transition: 'border-color 180ms ease, box-shadow 180ms ease',
            }}
          >
            <option value="athlete">{formatRole('athlete')}</option>
            <option value="coach">{formatRole('coach')}</option>
          </select>
          <p
            style={{
              fontSize: '12px',
              color: 'var(--color-text-muted)',
              margin: '4px 0 0',
              lineHeight: 1.4,
            }}
          >
            Pre-filled by your inviter — feel free to change it.
          </p>
        </div>

        {/* Display name */}
        <div className="mc-input-focus">
          <label
            htmlFor="register-display-name"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Your name
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="register-display-name"
              type="text"
              autoComplete="name"
              placeholder="How should we address you?"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoFocus
              disabled={isSubmitting}
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '10px',
                border: '1px solid var(--color-border-strong)',
                backgroundColor: 'var(--color-surface-raised-3)',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                padding: '0 40px 0 14px',
                boxSizing: 'border-box',
                transition: 'border-color 180ms ease, box-shadow 180ms ease',
              }}
            />
            <UserIcon
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '16px',
                height: '16px',
                color: 'var(--color-text-muted)',
                pointerEvents: 'none',
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Password */}
        <div className="mc-input-focus">
          <label
            htmlFor="register-password"
            className="text-label"
            style={{ display: 'block', color: 'var(--color-text-muted)', marginBottom: '6px' }}
          >
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isSubmitting}
              aria-describedby="register-password-hint"
              style={{
                width: '100%',
                height: '44px',
                borderRadius: '10px',
                border: `1px solid ${passwordTooShort ? 'var(--color-signal-bad)' : 'var(--color-border-strong)'}`,
                backgroundColor: 'var(--color-surface-raised-3)',
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                padding: '0 40px 0 14px',
                boxSizing: 'border-box',
                transition: 'border-color 180ms ease, box-shadow 180ms ease',
              }}
            />
            <PasswordToggleButton
              visible={showPassword}
              onToggle={() => setShowPassword((v) => !v)}
              disabled={isSubmitting}
              controls="register-password"
            />
          </div>
          <p
            id="register-password-hint"
            style={{
              fontSize: '12px',
              color: passwordTooShort
                ? 'var(--color-signal-bad)'
                : 'var(--color-text-muted)',
              margin: '4px 0 0',
              lineHeight: 1.4,
              transition: 'color 180ms ease',
            }}
          >
            {passwordTooShort
              ? 'Password must be at least 12 characters.'
              : '12+ characters with letters and digits.'}
          </p>
        </div>

        {/* Turnstile widget — populated via explicit window.turnstile.render()
            in the useEffect above. We deliberately do NOT use the cf-turnstile
            class or data-* attributes because auto-render races React's mount. */}
        {TURNSTILE_SITE_KEY && (
          <div>
            <div ref={turnstileContainerRef} />
            {turnstileRequired && !turnstileToken && (
              <p
                style={{
                  fontSize: '12px',
                  color: 'var(--color-text-muted)',
                  margin: '4px 0 0',
                  lineHeight: 1.4,
                }}
              >
                Complete the verification above to continue.
              </p>
            )}
          </div>
        )}

        {/* Submit button */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full mc-submit-btn"
          style={{
            borderRadius: '10px',
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '15px',
            letterSpacing: '0.1px',
            marginTop: '4px',
          }}
          disabled={isSubmitting || !isFormValid}
        >
          {isSubmitting ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Spinner size={16} />
              Creating account…
            </span>
          ) : (
            'Create account'
          )}
        </Button>

        {/* Legal footer */}
        <p
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted)',
            textAlign: 'center',
            margin: '4px 0 0',
            lineHeight: 1.6,
          }}
        >
          By creating an account you agree to our{' '}
          <a href="#" style={{ color: 'var(--color-text-secondary)', textDecoration: 'underline' }}>
            Terms
          </a>{' '}
          and{' '}
          <a href="#" style={{ color: 'var(--color-text-secondary)', textDecoration: 'underline' }}>
            Privacy Policy
          </a>
          .
        </p>
      </form>

      {/* Back link */}
      <div style={{ marginTop: '32px', borderTop: '1px solid var(--color-border-subtle)', paddingTop: '20px' }}>
        <p
          className="text-body-sm"
          style={{ color: 'var(--color-text-muted)', textAlign: 'center', margin: 0 }}
        >
          Already have an account?{' '}
          <a href="/login" className="mc-back-link">
            Sign in
          </a>
        </p>
      </div>
    </AuthShell>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

type PageState =
  | { kind: 'no_token' }
  | { kind: 'validating' }
  | { kind: 'valid'; invitation: InvitationPreview }
  | { kind: 'invalid'; message?: string; errorCode?: string };

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>(
    token ? { kind: 'validating' } : { kind: 'no_token' }
  );

  useEffect(() => {
    if (!token) {
      setPageState({ kind: 'no_token' });
      return;
    }

    setPageState({ kind: 'validating' });

    previewInvitation(token)
      .then((preview) => {
        if (preview.valid) {
          setPageState({ kind: 'valid', invitation: preview });
        } else {
          setPageState({ kind: 'invalid', message: preview.message, errorCode: preview.error });
        }
      })
      .catch(() => {
        // Network error or 404 — treat as invalid
        setPageState({ kind: 'invalid' });
      });
  }, [token]);

  switch (pageState.kind) {
    case 'no_token':
      return <NoTokenScreen />;
    case 'validating':
      return <ValidatingScreen />;
    case 'invalid':
      return <InvalidTokenScreen message={pageState.message} errorCode={pageState.errorCode} />;
    case 'valid':
      return <RegistrationForm token={token!} invitation={pageState.invitation} />;
  }
}
